import { Comment } from '../../client-requests/components/comments/comments.model';
import { CrlActivityEntry, CrlItemFile, CrlRequestDetail } from '../models';

/**
 * Sets which request the new CRL detail page is showing. Dispatched by the
 * route-params service and the legacy selection bridge; setting it to `null`
 * (navigated back to the list) clears the loaded detail.
 */
export class SetSelectedRequestId {
  static readonly type = '[CxpCrlDetail] Set Selected Request Id';
  constructor(public readonly id: number | null) {}
}

/**
 * Sets which item the side panel is showing — the `:itemId` of the named
 * `panel` outlet child (PP-3753). `null` means the panel is closed. Purely
 * navigational; the item content is read from `detailData` at selector time.
 */
export class SetSelectedItemId {
  static readonly type = '[CxpCrlDetail] Set Selected Item Id';
  constructor(public readonly itemId: number | null) {}
}

/**
 * Loads the full detail for a request via `ClientRequestServiceApiBridge`:
 * sets `detailStatus` to loading, then stores `detailData` (or marks error).
 */
export class LoadRequestDetail {
  static readonly type = '[CxpCrlDetail] Load Request Detail';
  constructor(public readonly id: number) {}
}

export class LoadRequestDetailSuccess {
  static readonly type = '[CxpCrlDetail] Load Request Detail Success';
  constructor(public readonly detail: CrlRequestDetail) {}
}

export class LoadRequestDetailFailure {
  static readonly type = '[CxpCrlDetail] Load Request Detail Failure';
  constructor(public readonly error: unknown) {}
}

export class ClearRequestDetail {
  static readonly type = '[CxpCrlDetail] Clear Request Detail';
}

/**
 * Loads the activity feed for a single item from the dedicated per-item endpoint
 * (via `ClientRequestServiceApiBridge.getItemActivity`). Dispatched by the
 * overlay container whenever the selected item id changes. Sets
 * `itemActivityLoadingItemId`, then caches the mapped `CrlActivityEntry[]` under
 * the item id so a later return to the item renders the cached feed instantly
 * while this fresh load runs.
 */
export class LoadItemActivity {
  static readonly type = '[CxpCrlDetail] Load Item Activity';
  constructor(public readonly itemId: number) {}
}

export class LoadItemActivitySuccess {
  static readonly type = '[CxpCrlDetail] Load Item Activity Success';
  constructor(
    public readonly itemId: number,
    public readonly entries: CrlActivityEntry[],
  ) {}
}

export class LoadItemActivityFailure {
  static readonly type = '[CxpCrlDetail] Load Item Activity Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * Loads the comments thread for a single item (via `CxpCrlCommentsService`).
 * Dispatched by the comments tab when its item changes. Sets
 * `itemCommentsLoadingItemId` and caches the resulting `Comment[]` under the
 * item id — same stale-while-revalidate behaviour as {@link LoadItemActivity}.
 */
export class LoadItemComments {
  static readonly type = '[CxpCrlDetail] Load Item Comments';
  constructor(public readonly itemId: number) {}
}

export class LoadItemCommentsSuccess {
  static readonly type = '[CxpCrlDetail] Load Item Comments Success';
  constructor(
    public readonly itemId: number,
    public readonly comments: Comment[],
  ) {}
}

export class LoadItemCommentsFailure {
  static readonly type = '[CxpCrlDetail] Load Item Comments Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * Posts a top-level comment (no `parentId`) or a reply against an item, then
 * refreshes that item's cached thread. Dispatched by the comments tab's
 * composer; the returned dispatch observable completes when the reload lands so
 * the composer can drive its own posting state.
 */
export class PostItemComment {
  static readonly type = '[CxpCrlDetail] Post Item Comment';
  constructor(
    public readonly itemId: number,
    public readonly text: string,
    public readonly parentId: string | null,
  ) {}
}

/**
 * Records the caller as having viewed the given comments (top-level or replies)
 * of an item, in one batched write. Dispatched by the comments tab once a
 * rendered comment has been on screen long enough to count as read; the row's
 * unviewed dot clears through the comments live query rather than any local edit.
 */
export class MarkItemCommentsViewed {
  static readonly type = '[CxpCrlDetail] Mark Item Comments Viewed';
  constructor(
    public readonly itemId: number,
    public readonly commentIds: string[],
  ) {}
}

/**
 * Lazily loads a single item's files + answer from `/item/detail` (via
 * `ClientRequestServiceApiBridge.getItemDetail`). Dispatched by the overlay
 * container when the selected item changes, and re-dispatched after a detail
 * reload so the open item re-hydrates. On success the files + answer are patched
 * onto that one item inside `detailData`.
 */
export class LoadItemDetail {
  static readonly type = '[CxpCrlDetail] Load Item Detail';
  constructor(public readonly itemId: number) {}
}

export class LoadItemDetailSuccess {
  static readonly type = '[CxpCrlDetail] Load Item Detail Success';
  constructor(
    public readonly itemId: number,
    public readonly files: CrlItemFile[],
    public readonly answer: string,
    public readonly isAnswerMasked: boolean,
  ) {}
}

export class LoadItemDetailFailure {
  static readonly type = '[CxpCrlDetail] Load Item Detail Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * Saves a questionnaire answer for an item (silent autosave on blur). The
 * backend persists the answer WITHOUT changing the item status — transitions
 * happen only via mark-as-finished / undo — so no optimistic patch is applied.
 * The submission model is per-item; there is no request-level submit.
 */
export class SaveItemAnswer {
  static readonly type = '[CxpCrlDetail] Save Item Answer';
  constructor(
    public readonly itemId: number,
    public readonly answer: string,
  ) {}
}

export class SaveItemAnswerSuccess {
  static readonly type = '[CxpCrlDetail] Save Item Answer Success';
  constructor(
    public readonly itemId: number,
    /** The stored text, so the item reads back what was sent rather than what it held. */
    public readonly answer: string,
  ) {}
}

export class SaveItemAnswerFailure {
  static readonly type = '[CxpCrlDetail] Save Item Answer Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * Removes an uploaded client file from an item (the row "X"). Backed by the
 * bridge's `removeFileFromItem` (`ClientRequestRemoveFile` — soft-deletes the
 * record, hard-deletes the DMS file). The endpoint returns no reloaded detail,
 * so the handler optimistically drops the file from `detailData` and rolls it
 * back on failure.
 */
export class RemoveFileFromItem {
  static readonly type = '[CxpCrlDetail] Remove File From Item';
  constructor(
    public readonly itemId: number,
    public readonly fileId: number,
  ) {}
}

export class RemoveFileFromItemSuccess {
  static readonly type = '[CxpCrlDetail] Remove File From Item Success';
  constructor(
    public readonly itemId: number,
    public readonly fileId: number,
  ) {}
}

export class RemoveFileFromItemFailure {
  static readonly type = '[CxpCrlDetail] Remove File From Item Failure';
  constructor(
    public readonly itemId: number,
    public readonly fileId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * Per-item "I'm Finished" action → item moves to In Review. Optimistically
 * patches the item status, calls the mark-as-finished endpoint, then reloads
 * the detail to reconcile; on failure the patch is rolled back.
 */
export class MarkItemFinished {
  static readonly type = '[CxpCrlDetail] Mark Item Finished';
  constructor(public readonly itemId: number) {}
}

export class MarkItemFinishedSuccess {
  static readonly type = '[CxpCrlDetail] Mark Item Finished Success';
  constructor(public readonly itemId: number) {}
}

export class MarkItemFinishedFailure {
  static readonly type = '[CxpCrlDetail] Mark Item Finished Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * Per-item Undo "I'm Finished" → item moves from In Review back to open. NOT
 * optimistic: the item id is flagged in `pendingUndoItemIds` (the card disables
 * the Undo link) until the endpoint responds. Success ALWAYS reloads the detail
 * — the endpoint silently skips items the caller isn't assigned to, so only a
 * reload reflects server truth — and clears the flag once the reload settles.
 */
export class UndoItemFinished {
  static readonly type = '[CxpCrlDetail] Undo Item Finished';
  constructor(public readonly itemId: number) {}
}

export class UndoItemFinishedSuccess {
  static readonly type = '[CxpCrlDetail] Undo Item Finished Success';
  constructor(public readonly itemId: number) {}
}

export class UndoItemFinishedFailure {
  static readonly type = '[CxpCrlDetail] Undo Item Finished Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/** Per-item "Mark as N/A" with an optional note. */
export class MarkItemNotApplicable {
  static readonly type = '[CxpCrlDetail] Mark Item Not Applicable';
  constructor(
    public readonly itemId: number,
    public readonly note: string | null,
  ) {}
}

export class MarkItemNotApplicableSuccess {
  static readonly type = '[CxpCrlDetail] Mark Item Not Applicable Success';
  constructor(public readonly itemId: number) {}
}

export class MarkItemNotApplicableFailure {
  static readonly type = '[CxpCrlDetail] Mark Item Not Applicable Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * The item was flagged not-applicable but did not reach In Review — the second
 * half of the two-call N/A flow failed. Distinct from `MarkItemNotApplicableFailure`
 * because the flag DID persist: the item must not be rolled back, only reconciled
 * with the server and reported.
 */
export class MarkItemNotApplicableSubmitFailure {
  static readonly type = '[CxpCrlDetail] Mark Item Not Applicable Submit Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/**
 * Per-item Undo "Mark as N/A" → the item returns to its open state. Mirrors the
 * two calls that marking N/A makes: clear the not-applicable flag, then undo the
 * In Review transition that flag carried the item into. NOT optimistic: the item
 * id is flagged in `pendingUndoItemIds` (the Undo link disables) until both
 * endpoints respond. Success ALWAYS reloads the detail for server truth, and
 * clears the flag once the reload settles.
 */
export class UndoItemNotApplicable {
  static readonly type = '[CxpCrlDetail] Undo Item Not Applicable';
  constructor(public readonly itemId: number) {}
}

export class UndoItemNotApplicableSuccess {
  static readonly type = '[CxpCrlDetail] Undo Item Not Applicable Success';
  constructor(public readonly itemId: number) {}
}

export class UndoItemNotApplicableFailure {
  static readonly type = '[CxpCrlDetail] Undo Item Not Applicable Failure';
  constructor(
    public readonly itemId: number,
    public readonly error: unknown,
  ) {}
}

/** `contactId` identifies an assignee, `secureLinkId` a guest — exactly one is set. */
export class RevokeItemAccess {
  static readonly type = '[CxpCrlDetail] Revoke Item Access';
  constructor(
    public readonly itemId: number,
    public readonly target: { contactId: number | null; secureLinkId: number | null },
  ) {}
}

/**
 * Invites a firm contact (matched by `email`) to one or more items of the
 * current request — the "Invite" modal's Send. One invite covers every item the
 * user ticked in the dialog's tree. NOT optimistic: we don't know the resolved
 * contact until the detail reloads, so the handler calls the bridge and, on
 * success, reloads the request detail so the new contact surfaces in the rail's
 * Client access. Errors propagate so the dialog can surface them and stay open.
 */
export class InviteContactToItems {
  static readonly type = '[CxpCrlDetail] Invite Contact To Items';
  constructor(
    public readonly itemIds: number[],
    public readonly email: string,
    public readonly displayName: string,
    public readonly message: string,
  ) {}
}
